import * as React from "react";
import {
  CurrencyCircleDollar,
  CalendarBlank,
  ChartBar,
  Star,
  Receipt,
  UserPlus,
  TrendUp,
  TrendDown,
  Minus,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { DashboardStat, DashboardStatIcon } from "./types";

const ICON_MAP: Record<DashboardStatIcon, PhosphorIcon> = {
  revenue: CurrencyCircleDollar,
  bookings: CalendarBlank,
  occupancy: ChartBar,
  rating: Star,
  avg_ticket: Receipt,
  new_customers: UserPlus,
};

export interface DashboardKpiCardProps {
  stat: DashboardStat;
  label: string;
  compareLabel: string;
  index?: number;
  className?: string;
}

export const DashboardKpiCard = React.memo(function DashboardKpiCard({
  stat,
  label,
  compareLabel,
  index = 0,
  className,
}: DashboardKpiCardProps) {
  const Icon = ICON_MAP[stat.icon];
  const reduceMotion = useReducedMotion();

  const trendStyles = {
    up: "bg-biz-success-subtle text-biz-success border-biz-success-border",
    down: "bg-biz-danger-subtle text-biz-danger border-biz-danger-border",
    neutral: "bg-biz-muted text-biz-foreground-muted border-biz-border",
  } as const;
  const TrendIcon = stat.trend === "up" ? TrendUp : stat.trend === "down" ? TrendDown : Minus;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16, delay: reduceMotion ? 0 : index * 0.04, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "group relative rounded-biz-card border border-biz-border bg-biz-surface-elevated p-4",
        "biz-shadow-sm transition-all duration-180 ease-biz-out",
        "hover:-translate-y-px hover:biz-shadow-md",
        "biz-motion-safe",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-biz-btn bg-biz-primary-subtle text-biz-primary-subtle-fg">
          <Icon className="h-5 w-5" weight="fill" aria-hidden />
        </div>
      </div>
      <p className="mt-3 text-xs font-medium uppercase tracking-wide text-biz-foreground-muted">
        {label}
      </p>
      <p className="mt-1 font-mono-num text-[26px] font-semibold leading-tight text-biz-foreground">
        {stat.value}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold",
            trendStyles[stat.trend],
          )}
        >
          <TrendIcon className="h-3 w-3" weight="bold" aria-hidden />
          <span className="font-mono-num">{stat.trendValue}</span>
        </span>
        <span className="truncate text-[11px] text-biz-foreground-muted">{compareLabel}</span>
      </div>
    </motion.div>
  );
});
